import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Clock, Plus, Trash2, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const HEURES_MENSUELLES = 173.33;
const CATEGORIES = [
  { key: 'heures_15', label: '15%', rate: 1.15, desc: '6 premières heures/semaine' },
  { key: 'heures_50', label: '50%', rate: 1.50, desc: 'Au-delà de 6h/semaine' },
  { key: 'heures_75', label: '75%', rate: 1.75, desc: 'Nuit ou jour de repos/férié' },
  { key: 'heures_100', label: '100%', rate: 2.00, desc: 'Nuit de jour férié' },
];

const fmt = (n) => (Math.round(n || 0)).toLocaleString('fr-FR');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const emptyForm = () => ({
  period_type: 'jour',
  date_debut: new Date().toISOString().split('T')[0],
  heures_15: 0, heures_50: 0, heures_75: 0, heures_100: 0,
  salaire_reference: 0,
});

export const OvertimeManagement = ({ company }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [formEmployee, setFormEmployee] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const [empRes, otRes] = await Promise.all([
        axios.get(`${API}/companies/${company.id}/employees?page=1&limit=500&include_inactive=false`, authHeaders()),
        axios.get(`${API}/companies/${company.id}/overtime?month=${month}&year=${year}`, authHeaders()),
      ]);
      setEmployees(empRes.data.employees || empRes.data || []);
      setEntries(otRes.data.entries || []);
      setTotals(otRes.data.totals_by_employee || {});
    } catch (e) {
      console.error('Erreur chargement heures sup:', e);
      toast.error('Erreur lors du chargement des heures supplémentaires');
    } finally {
      setLoading(false);
    }
  }, [company?.id, month, year]);

  useEffect(() => { loadData(); }, [loadData]);

  const defaultSalaireRef = (emp) => (emp?.salary_categoriel || 0) + (emp?.sursalaire || 0);

  const openForm = (emp) => {
    setFormEmployee(emp);
    setForm({ ...emptyForm(), salaire_reference: defaultSalaireRef(emp) });
  };

  const tauxHoraire = form.salaire_reference > 0 ? form.salaire_reference / HEURES_MENSUELLES : 0;
  const previewTotal = CATEGORIES.reduce((sum, c) => sum + (parseFloat(form[c.key]) || 0) * tauxHoraire * c.rate, 0);

  const handleSave = async () => {
    const totalHours = CATEGORIES.reduce((s, c) => s + (parseFloat(form[c.key]) || 0), 0);
    if (totalHours <= 0) { toast.error('Veuillez saisir au moins une heure supplémentaire'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/overtime`, {
        company_id: company.id,
        employee_id: formEmployee.id,
        period_type: form.period_type,
        date_debut: form.date_debut,
        heures_15: parseFloat(form.heures_15) || 0,
        heures_50: parseFloat(form.heures_50) || 0,
        heures_75: parseFloat(form.heures_75) || 0,
        heures_100: parseFloat(form.heures_100) || 0,
        salaire_reference: parseFloat(form.salaire_reference) || 0,
      }, authHeaders());
      toast.success(`Heures supplémentaires enregistrées pour ${formEmployee.first_name} ${formEmployee.last_name}`);
      setFormEmployee(null);
      loadData();
    } catch (e) {
      console.error('Erreur enregistrement heures sup:', e);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Supprimer cette saisie d\'heures supplémentaires ?')) return;
    try {
      await axios.delete(`${API}/overtime/${entryId}`, authHeaders());
      toast.success('Saisie supprimée');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!company) {
    return (
      <Card><CardContent className="py-12 text-center text-gray-500">
        Sélectionnez une entreprise pour gérer les heures supplémentaires.
      </CardContent></Card>
    );
  }

  const q = search.trim().toLowerCase();
  const filteredEmployees = q
    ? employees.filter(e => `${e.first_name} ${e.last_name} ${e.matricule || ''}`.toLowerCase().includes(q))
    : employees;
  const grandTotal = Object.values(totals).reduce((s, t) => s + (t.montant_total || 0), 0);

  return (
    <div className="space-y-4" data-testid="overtime-management">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-orange-600" />
            Heures supplémentaires — {company.name}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Majoration : 15% (6 premières h/sem) • 50% (au-delà) • 75% (nuit, repos/férié) • 100% (nuit de férié) — Taux horaire = salaire de référence / 173,33
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            data-testid="overtime-month-select"
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white cursor-pointer"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{new Date(year, i).toLocaleDateString('fr-FR', { month: 'long' })}</option>
            ))}
          </select>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || year)}
            className="w-24" data-testid="overtime-year-input" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="py-3">
            <p className="text-xs text-gray-600">Employés avec heures sup</p>
            <p className="text-xl font-bold text-gray-900" data-testid="overtime-employees-count">{Object.keys(totals).length}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="py-3">
            <p className="text-xs text-gray-600">Total du mois</p>
            <p className="text-xl font-bold text-orange-700" data-testid="overtime-grand-total">{fmt(grandTotal)} FCFA</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200 col-span-2 md:col-span-1">
          <CardContent className="py-3">
            <p className="text-xs text-gray-600">Saisies enregistrées</p>
            <p className="text-xl font-bold text-gray-900">{entries.length}</p>
          </CardContent>
        </Card>
      </div>

      <Input
        placeholder="🔍 Rechercher un employé par nom ou matricule..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="overtime-employee-search"
      />

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs text-gray-600">
                    <th className="px-4 py-3">Employé</th>
                    <th className="px-2 py-3 text-right">Taux horaire</th>
                    <th className="px-2 py-3 text-center">H 15%</th>
                    <th className="px-2 py-3 text-center">H 50%</th>
                    <th className="px-2 py-3 text-center">H 75%</th>
                    <th className="px-2 py-3 text-center">H 100%</th>
                    <th className="px-2 py-3 text-right">Montant total</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Aucun employé actif trouvé</td></tr>
                  )}
                  {filteredEmployees.map((emp) => {
                    const t = totals[emp.id];
                    const empEntries = entries.filter(e => e.employee_id === emp.id);
                    const expanded = expandedEmployee === emp.id;
                    return (
                      <React.Fragment key={emp.id}>
                        <tr className={`border-b hover:bg-orange-50/50 ${t ? 'bg-orange-50/30' : ''}`} data-testid={`overtime-row-${emp.id}`}>
                          <td className="px-4 py-2">
                            <p className="font-medium text-gray-900">{emp.first_name} {emp.last_name}</p>
                            <p className="text-xs text-gray-500">{emp.matricule || emp.position}</p>
                          </td>
                          <td className="px-2 py-2 text-right text-gray-700">{fmt(defaultSalaireRef(emp) / HEURES_MENSUELLES)} F/h</td>
                          <td className="px-2 py-2 text-center">{t?.heures_15 || '-'}</td>
                          <td className="px-2 py-2 text-center">{t?.heures_50 || '-'}</td>
                          <td className="px-2 py-2 text-center">{t?.heures_75 || '-'}</td>
                          <td className="px-2 py-2 text-center">{t?.heures_100 || '-'}</td>
                          <td className="px-2 py-2 text-right font-semibold text-orange-700" data-testid={`overtime-total-${emp.id}`}>
                            {t ? `${fmt(t.montant_total)} FCFA` : '-'}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50 mr-1"
                              onClick={() => openForm(emp)} data-testid={`overtime-add-btn-${emp.id}`}>
                              <Plus className="w-3 h-3 mr-1" /> Ajouter
                            </Button>
                            {empEntries.length > 0 && (
                              <Button size="sm" variant="ghost" onClick={() => setExpandedEmployee(expanded ? null : emp.id)}
                                data-testid={`overtime-expand-btn-${emp.id}`}>
                                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {expanded && empEntries.map((entry) => (
                          <tr key={entry.id} className="border-b bg-gray-50 text-xs" data-testid={`overtime-entry-${entry.id}`}>
                            <td className="px-4 py-2 pl-8 text-gray-600">
                              {entry.period_type === 'semaine' ? 'Semaine du ' : 'Le '}
                              {new Date(entry.date_debut + 'T00:00:00').toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-2 py-2 text-right text-gray-500">{fmt(entry.taux_horaire)} F/h</td>
                            <td className="px-2 py-2 text-center">{entry.heures_15 || '-'}</td>
                            <td className="px-2 py-2 text-center">{entry.heures_50 || '-'}</td>
                            <td className="px-2 py-2 text-center">{entry.heures_75 || '-'}</td>
                            <td className="px-2 py-2 text-center">{entry.heures_100 || '-'}</td>
                            <td className="px-2 py-2 text-right text-gray-700">{fmt(entry.montant_total)} FCFA</td>
                            <td className="px-4 py-2 text-right">
                              <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-6"
                                onClick={() => handleDelete(entry.id)} data-testid={`overtime-delete-btn-${entry.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {formEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !saving && setFormEmployee(null)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="overtime-form-dialog">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center">
                <Clock className="w-4 h-4 mr-2 text-orange-600" />
                Heures sup — {formEmployee.first_name} {formEmployee.last_name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Période de saisie</Label>
                  <select value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}
                    data-testid="overtime-period-select"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white cursor-pointer">
                    <option value="jour">Par jour</option>
                    <option value="semaine">Par semaine</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">{form.period_type === 'semaine' ? 'Début de semaine' : 'Date du jour'}</Label>
                  <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                    data-testid="overtime-date-input" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Salaire de référence heures sup (FCFA)</Label>
                <Input type="number" value={form.salaire_reference}
                  onChange={(e) => setForm({ ...form, salaire_reference: parseFloat(e.target.value) || 0 })}
                  data-testid="overtime-salaire-ref-input" />
                <p className="text-xs text-gray-500 mt-1">
                  Taux horaire : <span className="font-semibold text-orange-700">{fmt(tauxHoraire)} FCFA/h</span> (référence / 173,33)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((c) => (
                  <div key={c.key}>
                    <Label className="text-xs">Heures à {c.label}</Label>
                    <Input type="number" min="0" step="0.5" value={form[c.key]}
                      onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                      data-testid={`overtime-input-${c.key}`} />
                    <p className="text-[10px] text-gray-500 mt-0.5">{c.desc}</p>
                    {(parseFloat(form[c.key]) || 0) > 0 && (
                      <p className="text-[10px] font-medium text-orange-700">
                        = {fmt((parseFloat(form[c.key]) || 0) * tauxHoraire * c.rate)} FCFA
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="rounded-lg bg-orange-50 border border-orange-200 p-3 flex justify-between items-center">
                <span className="text-sm text-gray-700">Montant total calculé</span>
                <span className="text-lg font-bold text-orange-700" data-testid="overtime-preview-total">{fmt(previewTotal)} FCFA</span>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setFormEmployee(null)} disabled={saving} data-testid="overtime-cancel-btn">Annuler</Button>
                <Button className="bg-orange-600 hover:bg-orange-700" onClick={handleSave} disabled={saving} data-testid="overtime-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
